import CreditCardIcon from "@mui/icons-material/CreditCard";
import PixIcon from "@mui/icons-material/Pix";
import BoletoIcon from "@mui/icons-material/Receipt";
import { Card, Skeleton } from "@mui/material";
import Grid from "@mui/material/Grid2";
import React from "react";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import AlertBlock from "@/components/molecules/AlertBlock";
import useGetTalk2PayFees from "@/hooks/requests/t2Pay/use-get-talk2pay-fees";
import { T2PayHomeMessages } from "@/messages/platforms/talk2PayHome/t2pay-home.messages";
import { formatCurrency } from "@/helpers/currency-helpers";

import { RateCard } from "./ratesCard";

interface RatesProps {
  messages: T2PayHomeMessages;
}

export function RatesTab({ messages }: RatesProps) {
  const { data: fees, isLoading, error } = useGetTalk2PayFees();
  const boletoValue = fees?.payment.bankSlip.defaultValue ?? 1.99;
  const creditOpValue = fees?.payment.creditCard.operationValue ?? 0.49;
  const creditOne = fees?.payment.creditCard.oneInstallmentPercentage ?? 2.99;
  const creditUpToSix = fees?.payment.creditCard.upToSixInstallmentsPercentage ?? 3.49;
  const creditUpToTwelve = fees?.payment.creditCard.upToTwelveInstallmentsPercentage ?? 3.99;
  const debitPerc = fees?.payment.debitCard.defaultPercentage ?? 1.89;
  const debitOp = fees?.payment.debitCard.operationValue ?? 0.35;
  const pixFixed = fees?.payment.pix.fixedFeeValue ?? 1.99;

  return (
    <Card>
      <MDBox p={3}>
        <MDTypography variant="h5">
          {isLoading ? <Skeleton width="50%" /> : messages.ratesTabTitle}
        </MDTypography>
      </MDBox>

      <MDBox p={3} pt={0}>
        <Grid container spacing={2}>
          {isLoading ? (
            Array.from({ length: 5 }).map((_, index) => (
              <Grid key={index} size={{ xs: 12, sm: 6, md: 4 }}>
                <MDBox p={2}>
                  <Skeleton variant="rectangular" height={120} />
                  <Skeleton variant="text" sx={{ mt: 2 }} width="60%" />
                  <Skeleton variant="text" sx={{ mt: 1 }} width="40%" />
                </MDBox>
              </Grid>
            ))
          ) : (
            <>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <RateCard
                  icon={<BoletoIcon fontSize="large" />}
                  title={messages.ratesText3}
                  primaryTextList={[
                    {
                      primaryValue: `${formatCurrency(boletoValue)}`,
                      primaryText: messages.primaryTextBankSlip,
                    },
                  ]}
                  secondaryText={messages.ratesText5}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <RateCard
                  icon={<CreditCardIcon fontSize="large" />}
                  title={messages.ratesText6}
                  primaryTextList={[
                    {
                      primaryValue: `${creditOne}% + ${formatCurrency(creditOpValue)}`,
                      primaryText: messages.primaryTextCreditOneInstallment,
                    },
                    {
                      primaryValue: `${creditUpToSix}% + ${formatCurrency(creditOpValue)}`,
                      primaryText: messages.primaryTextCreditUpToSix,
                    },
                    {
                      primaryValue: `${creditUpToTwelve}% + ${formatCurrency(creditOpValue)}`,
                      primaryText: messages.primaryTextCreditUpToTwelve,
                    },
                  ]}
                  secondaryText={messages.ratesText10}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <RateCard
                  icon={<CreditCardIcon fontSize="large" />}
                  title={messages.ratesText11}
                  primaryTextList={[
                    {
                      primaryValue: `${debitPerc}% + ${formatCurrency(debitOp)}`,
                      primaryText: messages.primaryTextDebit,
                    },
                  ]}
                  secondaryText={messages.ratesText13}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <RateCard
                  icon={<PixIcon fontSize="large" />}
                  title={messages.ratesText14}
                  primaryTextList={[
                    {
                      primaryValue: `${formatCurrency(pixFixed)}`,
                      primaryText: messages.primaryTextPixDynamic,
                    },
                  ]}
                  secondaryText={messages.ratesText16}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <RateCard
                  icon={<PixIcon fontSize="large" />}
                  title={messages.ratesText17}
                  primaryTextList={[
                    {
                      primaryValue: `${formatCurrency(pixFixed)}`,
                      primaryText: messages.primaryTextPixStatic,
                    },
                  ]}
                  secondaryText={messages.ratesText20}
                  extraText={messages.ratesText19}
                />
              </Grid>
            </>
          )}
        </Grid>
      </MDBox>

      {error && !fees && (
        <AlertBlock
          title={messages.errorTitle}
          description={messages.errorDescription}
          alt={messages.errorAlt}
        />
      )}
    </Card>
  );
}
